import { addTime } from '~/utils/helpers/date';

const logo = `${process.env.APP_BASE_DOMAIN}/logo-tierra-ogimage.png`;
const date = new Date();

const authorMapper = author =>
  author.name.toLowerCase().includes('plataforma tierra')
    ? {
        '@type': 'Organization',
        name: STRUCTURED_DATA_STATE.name,
        logo,
        url: `${process.env.APP_BASE_DOMAIN}${AUTHOR_BASE_URL}/${author.slug}`
      }
    : {
        '@type': 'Person',
        name: author.name,
        url: `${process.env.APP_BASE_DOMAIN}${AUTHOR_BASE_URL}/${author.slug}`
      };

export const getStructuredDataArticle = ({ ctx, title, image }, isNews = false) => {
  const defaultAuthor = {
    '@type': 'Organization',
    name: STRUCTURED_DATA_STATE.name,
    logo,
    url: `${process.env.APP_BASE_DOMAIN}${AUTHOR_BASE_URL}/plataforma-tierra`
  };

  const defaultPublisher = {
    '@type': 'Organization',
    name: STRUCTURED_DATA_STATE.name,
    logo: {
      '@type': 'ImageObject',
      url: logo
    }
  };

  return {
    '@context': 'https://schema.org',
    '@type': isNews ? 'NewsArticle' : 'Article',
    headline: title,
    image,
    datePublished: ctx.published_date || date.toISOString(),
    author: ctx.author_role?.length ? ctx.author_role.map(authorMapper) : defaultAuthor,
    publisher: ctx.author_role?.length
      ? ctx.author_role.map(author =>
          author.name.toLowerCase().includes('plataforma tierra')
            ? defaultPublisher
            : {
                '@type': 'Person',
                name: author.name,
                url: `${process.env.APP_BASE_DOMAIN}${AUTHOR_BASE_URL}/${author.slug}`
              }
        )
      : defaultPublisher
  };
};

export const getStructuredDataEvent = ({ ctx, title, image, url, description }) => {
  const isWebinar = ctx.courseFormat && /webinar/.test(ctx.courseFormat);

  const structuredData = {
    '@context': 'http://www.schema.org',
    '@type': 'Event',
    name: title,
    startDate: ctx.list_of_sessions?.[0]?.date,
    image,
    location: isWebinar
      ? url
        ? { '@type': 'VirtualLocation', url }
        : undefined
      : {
          '@type': 'Place',
          name: ctx.campus?.name || title,
          sameAs: ctx.campus?.name || title,
          address: {
            '@type': 'PostalAddress',
            streetAddress: ctx.campus?.address || title
          },
          hasMap: ctx.campus?.google_maps_url
            ? {
                '@type': 'Map',
                mapType: 'GoogleMaps',
                url: ctx.campus.google_maps_url
              }
            : undefined
        },
    eventStatus: 'https://schema.org/EventScheduled',
    organizer: {
      '@type': 'Organization',
      name: 'Plataforma Tierra Cursos',
      url: process.env.APP_BASE_DOMAIN
    },
    offers: {
      '@type': 'Offer',
      url,
      price: ctx.event_features
        ? ctx.event_features.price * ((100 - ctx.event_features.discount) / 100)
        : undefined,
      priceCurrency: 'EUR',
      availability: 'https://schema.org/InStock',
      validFrom: ctx.list_of_sessions?.[0]?.date
    },
    performer: {
      '@type': 'PerformingGroup',
      name: 'Plataforma Tierra Cursos'
    },
    url,
    description,
    eventAttendanceMode: isWebinar
      ? 'https://schema.org/OnlineEventAttendanceMode'
      : 'https://schema.org/OfflineEventAttendanceMode'
  };

  if (isWebinar) {
    structuredData.additionalType = 'http://www.schema.org/EducationCourse';
  }

  const endDate = getEndDate(ctx);
  if (endDate) {
    structuredData.endDate = endDate;
  }

  return structuredData;
};

export const getStructuredDataAuthor = ({ ctx, image }) => ({
  '@context': 'https://schema.org',
  '@type': 'ProfilePage',
  mainEntity: {
    '@type': 'Person',
    name: ctx.name || 'Author',
    jobTitle: ctx.job || 'Author',
    url: `${process.env.APP_BASE_DOMAIN}${AUTHOR_BASE_URL}/${ctx.slug}`,
    image
  }
});

export const getStructuredDataHome = ({ title, image, description }) => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: title,
  url: 'https://www.plataformatierra.es',
  logo: image,
  sameAs: STRUCTURED_DATA_HOME.SAMEAS,
  contanctPoint: {
    '@type': 'ContactPoint',
    telephone: '+34 950580548',
    contactType: 'Customer Service',
    areaServed: 'ES',
    availableLanguage: 'Spanish'
  },
  description,
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Calle de la Innovación, 1',
    postalCode: '04130',
    addressLocality: 'Almería',
    addressCountry: 'ES'
  },
  actionableFeedbackPolicy: STRUCTURED_DATA_HOME.AVISOLEGAL,
  masthead: STRUCTURED_DATA_HOME.QUIENESSOMOS,
  missionCoveragePrioritiesPolicy: STRUCTURED_DATA_HOME.QUIENESSOMOS,
  memberOf: {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: STRUCTURED_DATA_HOME.ORGANIZATION
  }
});

export const getStructuredDataProduct = ({ ctx, title, image, description }) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: title,
  description,
  datePublished: '2023-01-15T11:14:10.166Z',
  image,
  author: {
    '@type': 'Organization',
    name: 'Plataforma Tierra',
    logo: ctx.header.logos[0]?.url,
    url: 'https://www.plataformatierra.es'
  },
  publisher: {
    '@type': 'Organization',
    name: 'Plataforma Tierra',
    logo: {
      '@type': 'ImageObject',
      url: ctx.header.logos[0]?.url
    },
    url: 'https://www.plataformatierra.es'
  },
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': ctx.header.links[0]?.url
  }
});

export const getStructuredDataInnovation = ({ ctx, title, image, description, url }) => {
  const defaultPublisher = {
    '@type': 'Organization',
    name: STRUCTURED_DATA_STATE.name,
    logo: {
      '@type': 'ImageObject',
      url: logo
    },
    url: `${process.env.APP_BASE_DOMAIN}${AUTHOR_BASE_URL}/plataforma-tierra`
  };

  let author;
  if (ctx.author_role?.length) {
    author = ctx.author_role.map(authorMapper);
  } else if (ctx.coordinator_role?.length) {
    author = ctx.coordinator_role.map(coordinator => ({
      '@type': 'Person',
      name: coordinator.name,
      url: `${process.env.APP_BASE_DOMAIN}${AUTHOR_BASE_URL}/${coordinator.slug}`,
      jobTitle: coordinator.job,
      image: coordinator.image?.url
    }));
  } else {
    author = {
      '@type': 'Organization',
      name: STRUCTURED_DATA_STATE.name,
      logo,
      url: `${process.env.APP_BASE_DOMAIN}${AUTHOR_BASE_URL}/plataforma-tierra`
    };
  }

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    '@id': url,
    url,
    name: title,
    headline: title,
    description: description || ctx.abstract,
    datePublished: ctx.published_date,
    dateModified: ctx.published_date,
    image,
    sameAs: url,
    author,
    publisher: ctx.author_role?.length
      ? ctx.author_role.map(author =>
          author.name.toLowerCase().includes('plataforma tierra')
            ? {
                '@type': 'Organization',
                name: STRUCTURED_DATA_STATE.name,
                logo: {
                  '@type': 'ImageObject',
                  url: logo
                }
              }
            : {
                '@type': 'Person',
                name: author.name,
                url: `${process.env.APP_BASE_DOMAIN}${AUTHOR_BASE_URL}/${author.slug}`
              }
        )
      : defaultPublisher
  };

  if (ctx.mediateca?.[0]?.isbn) {
    structuredData.associatedMedia = {
      '@type': 'Book',
      isbn: ctx.mediateca[0].isbn,
      name: title,
      description: description || ctx.abstract,
      url,
      author: ctx.author_role?.length ? ctx.author_role.map(authorMapper) : undefined,
      publisher: defaultPublisher,
      datePublished: ctx.published_date,
      image
    };
  }

  return structuredData;
};

export const getStructuredDataBreadcrumbs = ({ data, section, path }) => {
  if (!data)
    return {
      '@context': 'https://schema.org/',
      '@type': 'BreadcrumbList',
      itemListElement: []
    };

  const itemListElement = [
    {
      '@type': 'ListItem',
      position: 1,
      name: section,
      item: `${process.env.APP_BASE_DOMAIN}/${path}/`
    }
  ];

  if (data.label) {
    itemListElement.push({
      '@type': 'ListItem',
      position: 2,
      name: data.label
    });
  }

  return {
    '@context': 'https://schema.org/',
    '@type': 'BreadcrumbList',
    itemListElement
  };
};

export const getEndDate = ctx => {
  if (Object.entries(ctx).length === 0) {
    return (
      ctx?.external_form_integrations?.registration_datetime_limit ??
      ctx?.external_form_integrations?.registration_datetime_from
    );
  }

  if (ctx?.external_form_integrations?.registration_datetime_from) {
    return addTime(ctx.external_form_integrations.registration_datetime_from, 1, 'y').format();
  }

  if (
    !ctx?.external_form_integrations?.registration_datetime_from &&
    !ctx?.external_form_integrations?.registration_datetime_limit
  ) {
    return addTime(new Date(), 2, 'y').format();
  }

  return addTime(ctx?.external_form_integrations?.registration_datetime_limit, 1, 'y').format();
};
